import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js'
import * as dat from 'dat.gui'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

class ModelViewer {
	constructor(canvas, modelUrl, modelName) {
		this.canvas = canvas
		this.modelUrl = modelUrl
		this.modelName = modelName
		this.scene = null
		this.camera = null
		this.renderer = null
		this.model = null
		this.animationId = null
		this.gui = null
		this.lights = {}
		this.scrollTrigger = null

		// Get HDR environment URL from body data attribute
		this.environmentUrl = document.body.dataset.modelEnvironment

		// GUI settings object
		this.settings = {
			modelScale: 1.2,

			// Model position
			modelPositionX: 0,
			modelPositionY: 0.4,
			modelPositionZ: 0,

			// Model rotation
			modelRotationX: 0,
			modelRotationY: 0,
			modelRotationZ: 0,

			// Camera settings
			cameraDistance: 20,
			cameraHeight: 0.6,
			fov: 10,

			// Lighting settings
			ambientIntensity: 1.5,
			directionalIntensity: 4,
			directionalX: -15,
			directionalY: 8,
			directionalZ: 5,

			// Renderer settings
			toneMappingExposure: 1.5,
			shadowMapEnabled: false,

			// Background
			backgroundColor: '#ffffff',
			backgroundTransparent: true,

			// Environment
			environmentIntensity: 3
		}

		this.init()
	}

	init() {
		if (!this.canvas || !this.modelUrl) return

		// Create scene
		this.scene = new THREE.Scene()

		// Create camera with optimal settings
		const aspect = this.canvas.clientWidth / this.canvas.clientHeight
		this.camera = new THREE.PerspectiveCamera(this.settings.fov, aspect, 0.1, 1000)
		this.camera.position.set(0, this.settings.cameraHeight, this.settings.cameraDistance)

		// Create renderer with Three.js Editor quality settings
		this.renderer = new THREE.WebGLRenderer({
			canvas: this.canvas,
			antialias: true,
			alpha: true
		})

		// Essential quality settings used by Three.js Editor
		this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight)
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

		// Enable tone mapping for realistic colors
		this.renderer.toneMapping = THREE.ACESFilmicToneMapping
		this.renderer.toneMappingExposure = this.settings.toneMappingExposure
		this.renderer.outputColorSpace = THREE.SRGBColorSpace

		// Enable shadows with high quality
		this.renderer.shadowMap.enabled = this.settings.shadowMapEnabled
		this.renderer.shadowMap.type = THREE.PCFSoftShadowMap

		// Set up environment lighting
		this.setupEnvironmentLighting()

		// Load model
		this.loadModel()

		// Handle resize
		this.handleResize()

		// Set up dat.GUI
		this.setupGUI()

		// Start render loop
		this.animate()
	}

	setupGUI() {
		this.gui = new dat.GUI({ width: 300 })

		this.gui.close()

		// Model controls
		const modelFolder = this.gui.addFolder('Model')
		modelFolder
			.add(this.settings, 'modelScale', 0.1, 3)
			.name('Scale')
			.onChange((value) => {
				if (this.model) {
					this.model.scale.setScalar(value)
				}
			})

		// Model position controls
		const positionFolder = this.gui.addFolder('Model Position')
		positionFolder
			.add(this.settings, 'modelPositionX', -10, 10)
			.name('Position X')
			.onChange((value) => {
				if (this.model) {
					this.model.position.x = value
				}
			})
		positionFolder
			.add(this.settings, 'modelPositionY', -10, 10)
			.name('Position Y')
			.onChange((value) => {
				if (this.model) {
					this.model.position.y = value
				}
			})
		positionFolder
			.add(this.settings, 'modelPositionZ', -10, 10)
			.name('Position Z')
			.onChange((value) => {
				if (this.model) {
					this.model.position.z = value
				}
			})

		// Model rotation controls
		const rotationFolder = this.gui.addFolder('Model Rotation')
		rotationFolder
			.add(this.settings, 'modelRotationX', -Math.PI, Math.PI)
			.name('Rotation X')
			.onChange((value) => {
				if (this.model) {
					this.model.rotation.x = value
				}
			})
		rotationFolder
			.add(this.settings, 'modelRotationY', -Math.PI, Math.PI)
			.name('Rotation Y')
			.onChange((value) => {
				if (this.model && !this.scrollTrigger) {
					this.model.rotation.y = value
				}
			})
		rotationFolder
			.add(this.settings, 'modelRotationZ', -Math.PI, Math.PI)
			.name('Rotation Z')
			.onChange((value) => {
				if (this.model) {
					this.model.rotation.z = value
				}
			})

		// Camera controls
		const cameraFolder = this.gui.addFolder('Camera')
		cameraFolder
			.add(this.settings, 'fov', 10, 120)
			.name('FOV')
			.onChange((value) => {
				this.camera.fov = value
				this.camera.updateProjectionMatrix()
			})
		cameraFolder
			.add(this.settings, 'cameraDistance', 1, 20)
			.name('Distance')
			.onChange((value) => {
				this.updateCameraPosition()
			})
		cameraFolder
			.add(this.settings, 'cameraHeight', -10, 10)
			.name('Height')
			.onChange((value) => {
				this.updateCameraPosition()
			})

		// Lighting controls
		const lightingFolder = this.gui.addFolder('Lighting')
		lightingFolder
			.add(this.settings, 'ambientIntensity', 0, 4)
			.name('Ambient Intensity')
			.onChange((value) => {
				if (this.lights.ambient) {
					this.lights.ambient.intensity = value
				}
			})
		lightingFolder
			.add(this.settings, 'directionalIntensity', 0, 6)
			.name('Directional Intensity')
			.onChange((value) => {
				if (this.lights.directional) {
					this.lights.directional.intensity = value
				}
			})
		lightingFolder
			.add(this.settings, 'directionalX', -20, 20)
			.name('Light X')
			.onChange((value) => {
				if (this.lights.directional) {
					this.lights.directional.position.x = value
				}
			})
		lightingFolder
			.add(this.settings, 'directionalY', -20, 20)
			.name('Light Y')
			.onChange((value) => {
				if (this.lights.directional) {
					this.lights.directional.position.y = value
				}
			})
		lightingFolder
			.add(this.settings, 'directionalZ', -20, 20)
			.name('Light Z')
			.onChange((value) => {
				if (this.lights.directional) {
					this.lights.directional.position.z = value
				}
			})

		// Environment controls
		const environmentFolder = this.gui.addFolder('Environment')
		environmentFolder
			.add(this.settings, 'environmentIntensity', 0, 3)
			.name('Environment Intensity')
			.onChange((value) => {
				if (this.scene.environment) {
					this.scene.environment.intensity = value
				}
			})

		// Add transparency toggle first
		environmentFolder
			.add(this.settings, 'backgroundTransparent')
			.name('Transparent Background')
			.onChange((value) => {
				if (value) {
					this.scene.background = null
				} else {
					this.scene.background = new THREE.Color(this.settings.backgroundColor)
				}
			})

		// Add color picker (only works when transparency is off)
		environmentFolder
			.addColor(this.settings, 'backgroundColor')
			.name('Background Color')
			.onChange((value) => {
				if (!this.settings.backgroundTransparent) {
					this.scene.background = new THREE.Color(value)
				}
			})

		// Renderer controls
		const rendererFolder = this.gui.addFolder('Renderer')
		rendererFolder
			.add(this.settings, 'toneMappingExposure', 0, 3)
			.name('Exposure')
			.onChange((value) => {
				this.renderer.toneMappingExposure = value
			})
		rendererFolder
			.add(this.settings, 'shadowMapEnabled')
			.name('Shadows')
			.onChange((value) => {
				this.renderer.shadowMap.enabled = value
				if (this.lights.directional) {
					this.lights.directional.castShadow = value
				}
			})

		// Utility functions
		const utilsFolder = this.gui.addFolder('Utils')
		utilsFolder
			.add(
				{
					resetCamera: () => {
						this.settings.cameraDistance = 5
						this.settings.cameraHeight = 0
						this.settings.fov = 50
						this.updateCameraPosition()
						this.camera.fov = this.settings.fov
						this.camera.updateProjectionMatrix()
						this.gui.updateDisplay()
					}
				},
				'resetCamera'
			)
			.name('Reset Camera')

		utilsFolder
			.add(
				{
					exportSettings: () => {
						const settingsJson = JSON.stringify(this.settings, null, 2)
						console.log('Current settings:', settingsJson)
						navigator.clipboard.writeText(settingsJson)
						alert('Settings copied to clipboard!')
					}
				},
				'exportSettings'
			)
			.name('Export Settings')

		// Open some folders by default
		modelFolder.open()
		positionFolder.open()
		rotationFolder.open()
		lightingFolder.open()
	}

	updateCameraPosition() {
		const distance = this.settings.cameraDistance
		const height = this.settings.cameraHeight
		this.camera.position.set(0, height, distance) // Direct back view
		this.camera.lookAt(0, 0, 0)
	}

	setupEnvironmentLighting() {
		const pmremGenerator = new THREE.PMREMGenerator(this.renderer)

		// Load HDR environment if available, otherwise fallback to procedural
		if (this.environmentUrl) {
			const rgbeLoader = new RGBELoader()
			rgbeLoader.load(
				this.environmentUrl,
				(texture) => {
					const envMap = pmremGenerator.fromEquirectangular(texture).texture

					// Set environment map for realistic lighting and reflections
					this.scene.environment = envMap

					// Set transparent background instead of white
					this.scene.background = null

					// Cleanup
					texture.dispose()
					pmremGenerator.dispose()
				},
				(progress) => {
					console.log('HDR loading progress:', (progress.loaded / progress.total) * 100 + '%')
				},
				(error) => {
					console.error('Error loading HDR environment, falling back to procedural:', error)
					this.createProceduralEnvironment(pmremGenerator)
				}
			)
		} else {
			this.createProceduralEnvironment(pmremGenerator)
		}

		// Add ambient light
		this.lights.ambient = new THREE.AmbientLight(0xffffff, this.settings.ambientIntensity)
		this.scene.add(this.lights.ambient)

		// Add directional light
		this.lights.directional = new THREE.DirectionalLight(
			0xffffff,
			this.settings.directionalIntensity
		)
		this.lights.directional.position.set(
			this.settings.directionalX,
			this.settings.directionalY,
			this.settings.directionalZ
		)
		this.lights.directional.castShadow = this.settings.shadowMapEnabled
		this.lights.directional.shadow.mapSize.width = 2048
		this.lights.directional.shadow.mapSize.height = 2048
		this.lights.directional.shadow.camera.near = 0.1
		this.lights.directional.shadow.camera.far = 50
		this.lights.directional.shadow.camera.left = -10
		this.lights.directional.shadow.camera.right = 10
		this.lights.directional.shadow.camera.top = 10
		this.lights.directional.shadow.camera.bottom = -10
		this.lights.directional.shadow.bias = -0.0001
		this.scene.add(this.lights.directional)
	}

	createProceduralEnvironment(pmremGenerator) {
		// Create a simple gradient environment (fallback)
		const envMapSize = 256
		const envMapData = new Uint8Array(envMapSize * envMapSize * 4)

		for (let i = 0; i < envMapSize; i++) {
			for (let j = 0; j < envMapSize; j++) {
				const index = (i * envMapSize + j) * 4
				const y = i / envMapSize

				// Create a simple sky gradient
				const skyColor = new THREE.Color().lerpColors(
					new THREE.Color(0x87ceeb), // Sky blue
					new THREE.Color(0xffffff), // White
					Math.pow(y, 0.5)
				)

				envMapData[index] = skyColor.r * 255
				envMapData[index + 1] = skyColor.g * 255
				envMapData[index + 2] = skyColor.b * 255
				envMapData[index + 3] = 255
			}
		}

		const envMapTexture = new THREE.DataTexture(
			envMapData,
			envMapSize,
			envMapSize,
			THREE.RGBAFormat
		)
		envMapTexture.needsUpdate = true
		envMapTexture.mapping = THREE.EquirectangularReflectionMapping

		const envMap = pmremGenerator.fromEquirectangular(envMapTexture).texture

		// Set environment map for the scene
		this.scene.environment = envMap
		this.scene.background = null // Transparent background

		// Cleanup
		pmremGenerator.dispose()
		envMapTexture.dispose()
	}

	setupScrollTrigger() {

		this.canvas.parentElement.classList.add('ready') 

		console.log('setupScrollTrigger')
		console.log(this.canvas)

		if (!this.model) return

		// Find the product-info section
		const productSection = this.canvas.closest('.product-info')
		if (!productSection) {
			console.warn('Could not find .product-info section for ScrollTrigger')
			return
		}

		const illustrations = productSection.querySelectorAll('.product-info__image-illustration')

		if (this.scrollTrigger) {
			this.scrollTrigger.kill()
		}

		this.timeline = gsap.timeline({
			scrollTrigger: {
				trigger: productSection,
				start: 'top top',
				end: 'bottom bottom',
				scrub: 1.5,
				toggleActions: "restart pause resume pause"
			}
		})

		this.timeline.fromTo(this.model.rotation, {
			y: -Math.PI / 2,
			z: 0
		}, {
			y: Math.PI * 1.4,
			z: Math.PI / 6
		})

		illustrations.forEach((illustration) => {
			this.timeline.fromTo(illustration, {
				translate: illustration.dataset.from
			}, {
				translate: '0px 0px',
				ease: 'power1.inOut',
				overwrite: 'auto'
			}, '<')
		})
	}

	loadModel() {
		const loader = new GLTFLoader()

		// Set up Draco loader for compressed models
		const dracoLoader = new DRACOLoader()
		dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/')
		loader.setDRACOLoader(dracoLoader)

		loader.load(
			this.modelUrl,
			(gltf) => {
				this.model = gltf.scene

				console.log(this.model)

				this.canvas.parentElement.classList.add('loaded')

				// Process materials like Three.js Editor does
				this.model.traverse((child) => {

					console.log(child.name)

					if (child.isMesh) {
						child.castShadow = true
						child.receiveShadow = true

						// Ensure proper material setup
						if (child.material) {
							// Convert to PBR material if needed
							if (
								child.material.type === 'MeshBasicMaterial' ||
								child.material.type === 'MeshLambertMaterial'
							) {
								const oldMaterial = child.material
								child.material = new THREE.MeshStandardMaterial({
									map: oldMaterial.map,
									color: oldMaterial.color
								})
								oldMaterial.dispose()
							}

							// Enable anisotropic filtering for all textures
							const maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy()
							if (child.material.map) child.material.map.anisotropy = maxAnisotropy
							if (child.material.normalMap) child.material.normalMap.anisotropy = maxAnisotropy
							if (child.material.roughnessMap)
								child.material.roughnessMap.anisotropy = maxAnisotropy
							if (child.material.metalnessMap)
								child.material.metalnessMap.anisotropy = maxAnisotropy

							// Ensure proper encoding for textures
							if (child.material.map) child.material.map.colorSpace = THREE.SRGBColorSpace

							child.material.needsUpdate = true
						}
					}

					if (child.isMesh && child.material && child.name === 'Cylinder') {
						console.log(child)
						child.material.transparent = true;
						child.material.opacity = 0.4;
						child.material.metalness = 0.1;
						child.material.roughness = 0.1;
					} 

					if (child.isMesh && child.material && child.name === 'Jar') {
						console.log(child)
						child.material.transparent = true;
						child.material.opacity = 0.3;
						child.material.metalness = 0.8;
						child.material.roughness = 0.025;
					} 

					if (child.isMesh && child.material && child.name === 'Main_Glass_02') {
						console.log(child)
						child.material.transparent = true;
						child.material.opacity = 0.55;
						child.material.metalness = 0.8;
						child.material.roughness = 0.025;
					} 

					if (child.isMesh && child.material && child.name === 'Liquid_02') {
						console.log(child)
						child.material.transparent = true;
						child.material.opacity = 0;
						child.material.metalness = 0.8;
						child.material.roughness = 0.1;
					} 

					if (child.isMesh && child.material && child.name === 'RDD_Label') {
						console.log(child)
						// child.material.transparent = false;
						// child.material.opacity = 1;
						// child.material.metalness = 0;
						child.material.roughness = 0;
					} 

					if (child.isMesh && child.material && child.name === 'Cube') {
						console.log(child)
						child.material.transparent = false;
						child.material.opacity = 1;
						child.material.metalness = 0.05;
						child.material.roughness = 0.05;
					}
				})

				// Center and scale the model
				this.fitModelToView()

				this.scene.add(this.model)

				// Animate model entrance from bottom
				// this.animateModelEntrance()

				// Set up ScrollTrigger after model is loaded
				this.setupScrollTrigger()

				// Update scale control after model is loaded
				if (this.gui) {
					this.gui.updateDisplay()
				}
			},
			(progress) => {
				console.log('Loading progress:', (progress.loaded / progress.total) * 100 + '%')
			},
			(error) => {
				console.error('Error loading model:', error)
			}
		)
	}

	animateModelEntrance() {
		if (!this.model) return

		// Store the final position
		const finalPosition = {
			x: this.model.position.x,
			y: this.model.position.y,
			z: this.model.position.z
		}

		// Set initial position (move model down and slightly back)
		this.model.position.y = finalPosition.y - 5
		this.model.position.z = finalPosition.z - 2

		// Start with model invisible
		this.model.visible = false

		// Animate to final position with GSAP
		const tl = gsap.timeline({
			ease: "power2.out"
		})

		// Make model visible and animate position
		tl.set(this.model, { visible: true })

		tl.to(this.model.position, {
			duration: 1.2,
			y: finalPosition.y,
			z: finalPosition.z,
			ease: "power2.out"
		})

		// Optional: Add a subtle scale animation for extra polish
		tl.fromTo(this.model.scale,
			{
				x: this.model.scale.x * 0.8,
				y: this.model.scale.y * 0.8,
				z: this.model.scale.z * 0.8
			},
			{
				duration: 1.0,
				x: this.model.scale.x,
				y: this.model.scale.y,
				z: this.model.scale.z,
				ease: "back.out(1.2)"
			},
			"-=1.0" // Start at the same time as position animation
		)
	}

	fitModelToView() {
		if (!this.model) return

		// Calculate bounding box
		const box = new THREE.Box3().setFromObject(this.model)
		const center = box.getCenter(new THREE.Vector3())
		const size = box.getSize(new THREE.Vector3())

		// Center the model
		this.model.position.x = -center.x
		this.model.position.y = -center.y
		this.model.position.z = -center.z

		// Apply GUI position settings
		this.model.position.x += this.settings.modelPositionX
		this.model.position.y += this.settings.modelPositionY
		this.model.position.z += this.settings.modelPositionZ

		// // Apply GUI rotation settings ONLY if ScrollTrigger is not active
		this.model.rotation.x = this.settings.modelRotationX
		// if (!this.scrollTrigger) {
		// 	this.model.rotation.y = this.settings.modelRotationY
		// }
		this.model.rotation.z = this.settings.modelRotationZ

		// Scale model to fit in view
		const maxDim = Math.max(size.x, size.y, size.z)

		let scale = 0

		if (this.modelName.includes('Candle')) {
			scale = 1.75 / maxDim
			this.model.position.y -= 0.35
		} else if (this.modelName.includes('Diffuser')) {
			scale = 2.75 / maxDim
			this.model.position.y -= 0.4
		} else {
			scale = 2.75 / maxDim
		}
		
		this.model.scale.setScalar(scale)
		this.settings.modelScale = scale

		this.updateCameraPosition()
	}

	handleResize() {
		const resizeObserver = new ResizeObserver((entries) => {
			for (let entry of entries) {
				const { width, height } = entry.contentRect

				this.camera.aspect = width / height
				this.camera.updateProjectionMatrix()
				this.renderer.setSize(width, height)
			}
		})

		resizeObserver.observe(this.canvas)
	}

	animate() {
		const clock = new THREE.Clock()
		let previousTime = 0

		const tick = () => {
			const elapsedTime = clock.getElapsedTime()
			const deltaTime = elapsedTime - previousTime
			previousTime = elapsedTime

			this.renderer.render(this.scene, this.camera)

			window.requestAnimationFrame(tick)
		}

		tick()
	}

	destroy() {
		if (this.animationId) {
			cancelAnimationFrame(this.animationId)
		}

		if (this.scrollTrigger) {
			this.scrollTrigger.kill()
		}

		if (this.gui) {
			this.gui.destroy()
		}

		if (this.renderer) {
			this.renderer.dispose()
		}

		if (this.model) {
			this.scene.remove(this.model)
		}
	}
}

export const initProduct = () => {
	const modelCanvases = document.querySelectorAll('.product-info__canvas[data-model]')

	modelCanvases.forEach((container) => {
		const canvas = container.querySelector('canvas')
		const modelUrl = container.dataset.model
		const modelName = container.dataset.modelName

		if (canvas && modelUrl) {
			new ModelViewer(canvas, modelUrl, modelName)
		}
	})
}

export default () => {}